import { Navigate, useLocation, useParams } from "react-router-dom";
import { BRAND } from "@/lib/site";
import { SectionHead, usePageMeta } from "@/components/shared";
import { Reveal } from "@/components/motion-primitives";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: [
      ["What we collect", "When you submit a form on this website — a property assessment, cleaning quote, consultation request, guest support request or contact message — we collect the information you provide, such as your name, contact details and property information."],
      ["How we use it", "We use this information solely to respond to your enquiry, prepare assessments and quotes, and deliver our property services. We do not sell, rent or share your personal information with third parties for marketing purposes."],
      ["Property & guest confidentiality", "Property details, access information and booking data are shared only with verified owners and guests through secure channels. They are never published or disclosed to website visitors."],
      ["Owner portal", "The owner portal is protected by encrypted authentication. Dashboard information is visible only to the authenticated owner of the account."],
      ["Contact", `Questions about your information? Email ${BRAND.email} or call ${BRAND.phone}.`],
    ],
  },
  terms: {
    title: "Terms of Service",
    body: [
      ["Our services", `${BRAND.name} provides vacation-rental co-hosting, property management, cleaning, inspection and maintenance-coordination services in Bermuda. Specific services, rates and responsibilities are defined in a written agreement with each property owner before work begins.`],
      ["Ownership & control", "Property owners retain full ownership and control of their listings, accounts and properties at all times. Co-hosting and management services are performed with the owner's explicit authorization."],
      ["Third-party charges", "Cleaning, contractor work and materials are charged separately from management fees. Significant work requires owner authorization except under an emergency procedure agreed in writing."],
      ["Website content", "Content on this website is provided for general information. Sample dashboard figures in the owner portal are illustrative and marked as demo data."],
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    body: [
      ["Not legal advice", `${BRAND.name} assists homeowners with organizing information and preparing properties for Bermuda's vacation-rental registration process. We do not provide legal, tax, insurance or regulatory advice.`],
      ["Owner responsibility", "The property owner remains solely responsible for all legal, regulatory, registration, certification, insurance, tax and ownership requirements relating to their property and its operation as a vacation rental."],
      ["Accuracy", "We work hard to keep information on this website accurate and current, but requirements and market conditions change. Always verify regulatory obligations with the Bermuda Tourism Authority and qualified professional advisors."],
      ["Photography credits", "All photography on this site shows the real Bermuda, used under Creative Commons licenses with thanks to: Captain-tucker (CC BY-SA, St. George's aerial) · Ekem (CC BY-SA, Horseshoe Bay) · Andrew Currie (CC BY-SA, infinity pool) · Vernaccia (CC BY-SA, Hamilton waterfront) · Wendy Piersall (CC BY) · StormyDog (CC BY) · kansasphoto (CC BY) · purdyrns (CC BY) · mmmavocado (CC BY) · leechantmcarthur (CC BY) · Sandra Burrowes (CC BY)."],
    ],
  },
};

export default function Legal() {
  const { slug: paramSlug } = useParams();
  const { pathname } = useLocation();
  const slug = paramSlug || pathname.replace("/", "");
  const page = CONTENT[slug];
  usePageMeta(page?.title);
  if (!page) return <Navigate to="/" replace />;
  return (
    <main>
      <section className="mx-auto max-w-[820px] px-6 pb-28 pt-40 lg:px-10">
        <SectionHead overline="Legal" title={page.title} />
        <div className="mt-12 space-y-10" data-testid={`legal-${slug}`}>
          {page.body.map(([h, p], i) => (
            <Reveal key={h} delay={i * 0.05}>
              <div>
                <h2 className="font-serif text-2xl text-navy">{h}</h2>
                <p className="mt-3 text-sm leading-[1.85] text-navy/65">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
